import React, { FunctionComponent } from 'react'
import { Col, Row } from 'react-grid-system'
import { useTranslation } from 'react-i18next'

import { NoPatients } from './NoPatients/NoPatients'
import {
  StyledClinTableOrderToggleWrapper,
  StyledPatientDetail,
  StyledTooltipFilterWrapper
} from './PatientDashboard.styles'
import { IColumn } from './PatientDashboardContainer'
import { ClinTheme } from '../../../ClinTheme'
import { ClinChip } from '../../../components/ClinChip'
import { ChipState } from '../../../components/ClinChip/ClinChip.styles'
import {
  INewFeature,
  NewFeatureElements
} from '../../../components/ClinNewFeatureTooltip/ClinNewFeatureTooltip.types'
import { ClinNewFeatureTooltipContainer } from '../../../components/ClinNewFeatureTooltip/ClinNewFeatureTooltipContainer'
import { ClinPageContentFrame } from '../../../components/ClinPageContentFrame'
import { ClinPagination } from '../../../components/ClinPagination'
import {
  getCurrentPage,
  getTotalPages,
  IPagination
} from '../../../components/ClinPagination/ClinPagination.model'
import { ClinSpacer } from '../../../components/ClinSpacer/ClinSpacer'
import { ClinSpinner } from '../../../components/ClinSpinner'
import { StyledSpinnerContainer } from '../../../components/ClinSpinner/ClinSpinner.styles'
import {
  ClinPatientTableHeaderCell,
  ClinTable,
  ClinTableBodyCell,
  ClinTableRow
} from '../../../components/ClinTable/ClinTable'
import { ClinTableOrderToggle } from '../../../components/ClinTableOrderToggle'
import { SortDirectionType } from '../../../components/ClinTableOrderToggle/ClinTableOrderToggle'
import { ClinText } from '../../../components/ClinText'
import { TypographyVariant } from '../../../components/ClinText/ClinText.styles'
import { PatientSummaryDto } from '../../../types/swaggerTypes'
import { filterOptions, PatientColumn, sortOption } from '../Patients.model'
import { StyledFilter, StyledFilterWrapper } from '../PatientsFilter.styles'
import { PatientsSearch } from '../PatientsSearch'
import { Tooltip } from 'react-tippy'
import analyticsServiceSingleton from '../../../services/Analytics/initAnalytics'

interface IMyPatientsProps {
  /** The array of physicians search results */
  patients?: PatientSummaryDto[]
  loadedPatients?: boolean
  /** Whether table is loading or not */
  isLoading?: boolean
  /** Columns to show on the physicians table */
  columns: IColumn[]
  /** Pagination */
  pagination?: IPagination
  /** User country */
  userCountry?: string
  query?: string
  selectedSortOption?: sortOption
  selectedFilterOptions?: string[]
  showTableInTab?: boolean
  newFeaturePatientNumber?: string
  /** The selected column */
  selectedSortColumn?: string
  /** The selected sort direction */
  selectedSortDirection?: SortDirectionType | string
  newFeaturesList?: { [id: string]: INewFeature }
  isNoPatientsAlternate?: boolean
  isProgramOpenForResupply?: boolean
  /** Patients tab of a PTA programme: HCPs cannot create patients (CLOS-14456) */
  isPostTrialAccessProgram?: boolean
  /** When table row clicked */
  handleRowClicked?: (
    physicianId: string | number,
    patientId: string | number,
    programId?: string | number,
    isStock?: boolean,
    orderNumber?: string,
    orderStatus?: string
  ) => void
  /** When pagination page is selected */
  handlePageClicked?: (selectedPageIndex: number) => void
  /** Select results per page */
  handlePageSizeChange?: (pageSize: number) => void
  handleOnSearch?: (query: string) => void
  handleSetSelectedFilterOption: (option: string) => void
  handleSelectedSortOption: (option: sortOption) => void
  /** Handle column sort toggle */
  handleToggleSort?: (columnName: string | undefined) => void
  handleCloseFeatureHighlight?: (feature: NewFeatureElements) => void
  displayNewFeature?: (feature: NewFeatureElements) => boolean | undefined
  displayNewFeatureGlowEffect?: (
    feature: NewFeatureElements
  ) => boolean | undefined
  patientFilterChecked: boolean | null
}

const PatientDashboard: FunctionComponent<IMyPatientsProps> = ({
  patients,
  loadedPatients,
  isLoading,
  columns,
  pagination,
  selectedFilterOptions,
  showTableInTab,
  query,
  newFeaturePatientNumber,
  selectedSortColumn,
  selectedSortDirection,
  newFeaturesList,
  isNoPatientsAlternate,
  isProgramOpenForResupply,
  isPostTrialAccessProgram,
  displayNewFeature,
  displayNewFeatureGlowEffect,
  handleRowClicked,
  handlePageClicked,
  handlePageSizeChange,
  handleOnSearch,
  handleSetSelectedFilterOption,
  handleToggleSort,
  handleCloseFeatureHighlight,
  patientFilterChecked
}) => {
  const doPatientsExist = patients && patients.length > 0

  const { t } = useTranslation()

  return (
    <StyledPatientDetail>
      <PatientsSearch
        handleOnChange={handleOnSearch}
        handleMyPatinets={handleSetSelectedFilterOption}
        handleCloseFeatureHighlight={handleCloseFeatureHighlight}
        displayNewFeature={displayNewFeature}
        displayNewFeatureGlowEffect={displayNewFeatureGlowEffect}
        showTableInTab={showTableInTab}
        newFeaturesList={newFeaturesList}
        initialQuery={query}
        patientFilterChecked={patientFilterChecked}
      ></PatientsSearch>

      <ClinPageContentFrame hideBreadcrumbs={true}>
        {!showTableInTab && (
          <>
            <ClinSpacer height={26} />
            <StyledFilterWrapper>
              <StyledFilter>
                <>
                  {newFeaturesList &&
                    displayNewFeature &&
                    displayNewFeature(NewFeatureElements.PatientsFilter) && (
                      <StyledTooltipFilterWrapper>
                        <ClinNewFeatureTooltipContainer
                          feature={
                            newFeaturesList[NewFeatureElements.PatientsFilter]
                          }
                          openTooltipInitially={
                            !newFeaturesList[NewFeatureElements.PatientsFilter]
                              ?.isCancelled
                          }
                          showFeatureFromTable={true}
                          disableDismiss={true}
                          total={4}
                          current={3}
                        ></ClinNewFeatureTooltipContainer>
                      </StyledTooltipFilterWrapper>
                    )}
                  <ClinText
                    marginRight={`${ClinTheme.space[3]}px`}
                    lineHeight={ClinTheme.lineHeights.small}
                    color={'#280172'}
                    variant={TypographyVariant.H5}
                  >
                    {t('patient_detail:filter_by')}
                  </ClinText>
                </>
                {filterOptions?.map((filter, index) => {
                  const isActive =
                    (selectedFilterOptions?.findIndex(
                      (i) => i === filter.optionId
                    ) ?? -1) > -1

                  const showGlow =
                    newFeaturesList &&
                    displayNewFeatureGlowEffect?.(
                      NewFeatureElements.PatientsFilter
                    )

                  const handleClick = () => {
                    if (newFeaturesList?.[NewFeatureElements.PatientsFilter]) {
                      handleCloseFeatureHighlight?.(
                        NewFeatureElements.PatientsFilter
                      )
                    }
                    handleSetSelectedFilterOption(filter.optionId)

                    analyticsServiceSingleton.trackEvent(filter.event, {
                      filterId: filter.optionId,
                      filterName: filter.mixPanelLabel,
                      isActive: !isActive
                    })
                  }

                  return (
                    <Tooltip
                      key={filter.optionId}
                      theme="light"
                      position="top"
                      trigger="mouseenter"
                      arrow={true}
                      title={t(
                        `patient_detail:${filter.optionId}_filter_tooltip`
                      )}
                    >
                      <ClinChip
                        key={index}
                        showGlowEffect={showGlow}
                        state={isActive ? ChipState.Active : ChipState.Default}
                        isInteractive={true}
                        onClick={handleClick}
                      >
                        {t(filter.optionValue)}
                      </ClinChip>
                    </Tooltip>
                  )
                })}
              </StyledFilter>
            </StyledFilterWrapper>
          </>
        )}
        {!showTableInTab ? (
          <ClinSpacer height={24} hasBorder={true} />
        ) : (
          <ClinSpacer height={5} />
        )}
        <Row>
          <Col sm={12} style={showTableInTab ? { padding: 0 } : {}}>
            {isLoading || !loadedPatients ? (
              <StyledSpinnerContainer>
                <ClinSpinner size={ClinTheme.space[7]} />
              </StyledSpinnerContainer>
            ) : doPatientsExist ? (
              <ClinTable
                borderColapseSeparate={true}
                borderSpacing="0 8px"
                padding="0px 2px 0px 2px"
                tableHeader={() =>
                  columns.map((column: IColumn, index: number) => {
                    return (
                      <ClinPatientTableHeaderCell
                        key={index}
                        width={column.width}
                        background={ClinTheme.colors.lightGrey}
                        color={showTableInTab ? ClinTheme.colors.darkGrey : ''}
                      >
                        {column.isSortable ? (
                          <StyledClinTableOrderToggleWrapper>
                            <ClinTableOrderToggle
                              sortDirection={
                                selectedSortColumn ===
                                (column.name as PatientColumn)
                                  ? (selectedSortDirection as SortDirectionType)
                                  : SortDirectionType.None
                              }
                              onClick={() =>
                                handleToggleSort &&
                                handleToggleSort(column.name)
                              }
                            >
                              {column.title.toUpperCase()}
                            </ClinTableOrderToggle>
                          </StyledClinTableOrderToggleWrapper>
                        ) : (
                          <StyledClinTableOrderToggleWrapper>
                            <ClinTableOrderToggle hideSortIcon={true}>
                              {column.title.toUpperCase()}
                            </ClinTableOrderToggle>
                          </StyledClinTableOrderToggleWrapper>
                        )}
                      </ClinPatientTableHeaderCell>
                    )
                  })
                }
                className="TableOverflowVisible"
              >
                {patients?.map((patient: PatientSummaryDto, rowIndex) => {
                  return (
                    <ClinTableRow
                      style={
                        patient.patientNumber === newFeaturePatientNumber
                          ? {
                              pointerEvents: 'auto',
                              zIndex: 5,
                              position: 'relative'
                            }
                          : {}
                      }
                      background={ClinTheme.colors.white}
                      key={rowIndex}
                      onRowClick={() =>
                        handleRowClicked &&
                        patient.physicianId &&
                        handleRowClicked(
                          patient.physicianId,
                          patient.patientId,
                          patient.programId,
                          patient.stockOrderFlag,
                          patient.orderNumber,
                          patient.patientOrderStatus
                        )
                      }
                      onRowEnter={() =>
                        handleRowClicked &&
                        patient.physicianId &&
                        handleRowClicked(
                          patient.physicianId,
                          patient.patientId,
                          patient.programId,
                          patient.stockOrderFlag,
                          patient.orderNumber,
                          patient.patientOrderStatus
                        )
                      }
                    >
                      {columns.map((column: IColumn, index: number) => (
                        <ClinTableBodyCell
                          key={index}
                          style={{ padding: '11px 24px 11px 16px' }}
                        >
                          {column.renderValue(patient, rowIndex)}
                        </ClinTableBodyCell>
                      ))}
                    </ClinTableRow>
                  )
                })}
              </ClinTable>
            ) : (
              <NoPatients
                isAlternate={isNoPatientsAlternate}
                isResupply={isProgramOpenForResupply}
                hideCreatePatientLink={isPostTrialAccessProgram}
              />
            )}
          </Col>
        </Row>
        <ClinSpacer />
        <Row>
          <Col>
            {!isLoading &&
            doPatientsExist &&
            pagination &&
            getTotalPages(pagination) ? (
              <ClinPagination
                showTabsPagination={showTableInTab}
                showLeftSpacer={showTableInTab}
                currentPage={getCurrentPage(pagination)}
                totalPages={getTotalPages(pagination)}
                pageSize={pagination.take}
                handlePageSelected={(pageIndex) =>
                  handlePageClicked && handlePageClicked(pageIndex)
                }
                handlePageSizeChange={handlePageSizeChange}
              />
            ) : (
              <></>
            )}
          </Col>
        </Row>
      </ClinPageContentFrame>
    </StyledPatientDetail>
  )
}

export default PatientDashboard
